import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args as BaseArgs } from 'webapp/components/designer/left-panel/section-inner-base';
import DesignerLeftPanelSectionInnerBaseComponent from 'webapp/components/designer/left-panel/section-inner-base';

import { EmptyBlockSetup } from 'webapp/services/model-manager/blocks';

interface VisualizationOption {
	id: EmptyBlockSetup;
	label: string;
	icon: IconName;
}
interface Args extends BaseArgs {
	isOpen: boolean;
}

export default class DesignerLeftPanelVisualizationsComponent<
	T extends Args,
> extends DesignerLeftPanelSectionInnerBaseComponent<T> {
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	availableTourId = TourId.DESIGNER__SELECT_VISUALIZATION;

	options: VisualizationOption[] = [
		{ id: EmptyBlockSetup.NUMBER, label: 'Number', icon: 'Metrics' },
		{ id: EmptyBlockSetup.LINE_CHART, label: 'Line', icon: 'ChartLine' },
		{ id: EmptyBlockSetup.BAR_CHART, label: 'Bar', icon: 'ChartBar' },
		{ id: EmptyBlockSetup.COMBO_CHART, label: 'Combo', icon: 'ChartCombo' },
		{ id: EmptyBlockSetup.PIE, label: 'Pie', icon: 'ChartPie' },
		{ id: EmptyBlockSetup.TABLE, label: 'Table', icon: 'Table' },
		{ id: EmptyBlockSetup.LEADERBOARD, label: 'Leaderboard', icon: 'Trophy' },
		{ id: EmptyBlockSetup.PIPELINE, label: 'Pipeline', icon: 'ChartPipeline' },
		{ id: EmptyBlockSetup.FUNNEL, label: 'Funnel', icon: 'ChartFunnel' },
		{ id: EmptyBlockSetup.HEATMAP, label: 'Heatmap', icon: 'ChartHeatmap' },
		{ id: EmptyBlockSetup.PROGRESS, label: 'Progress', icon: 'ChartProgress' },
		{ id: EmptyBlockSetup.RADIAL_PROGRESS, label: 'Radial', icon: 'ChartRadial' },
		{ id: EmptyBlockSetup.GAUGE, label: 'Gauge', icon: 'ChartGauge' },
		{
			id: EmptyBlockSetup.HORIZONTAL_BAR_CHART,
			label: 'Horiz. bar',
			icon: 'ChartBarHorizontal',
		},
		{ id: EmptyBlockSetup.SPIDER, label: 'Spider', icon: 'ChartSpider' },
		{ id: EmptyBlockSetup.BUBBLE, label: 'Bubble', icon: 'ChartBubble' },
		{ id: EmptyBlockSetup.DATA_STORY, label: 'Data story', icon: 'DataStory' },
		{ id: EmptyBlockSetup.TEXT, label: 'Notes', icon: 'ChartText' },
		{ id: EmptyBlockSetup.IMAGE, label: 'Image', icon: 'Image' },
		{
			id: EmptyBlockSetup.AI_SUMMARY,
			label: 'AI summary',
			icon: 'AiSummary',
		},
	];

	checkTourStartConditions() {
		return isAlive(this) && this.args.isPanelOpen && this.args.isOpen;
	}

	@action
	initDraggable(node: HTMLElement) {
		this.listNode = node;
		this.initDraggableOn(node);
	}

	@action
	destroyDraggable() {
		this.cleanupDraggable();
	}

	@action
	onClick(setupId: EmptyBlockSetup) {
		void this.addNewBlock({}, setupId);
	}
	<template>
		<ul
			class="vis-list"
			{{didInsert this.initDraggable}}
			{{willDestroy this.destroyDraggable}}
			{{didUpdate this.checkToEndTour @isPanelOpen}}
		>
			{{#each this.options as |vis|}}
				<li class="vis-item datablock" data-setupid={{vis.id}} data-metrictype="basic">
					<a {{on "click" (fn this.onClick vis.id)}}>
						<CommonUiIconComponent @name={{vis.icon}} @size="lg" />
						{{vis.label}}
					</a>
				</li>
			{{/each}}
		</ul>
	</template>
}
